'use client'

import { useMemo, useState } from 'react'
import { Globe, TrendingUp, TrendingDown, Download, Search } from 'lucide-react'
import type { SavedRun } from '@/types/radar'
import { GRADE_COLORS, gradeFromScore } from '@/lib/radar/scoring'

/**
 * Multi-domain agency dashboard.
 * Shows all saved domains in a single table with scores, trends, and CSV export.
 * This is the screen agencies see first - not the single-domain audit flow.
 */
export function MultiDomainDashboard({
  savedRuns,
  onSelectDomain,
}: {
  savedRuns: SavedRun[]
  onSelectDomain: (run: SavedRun) => void
}) {
  const [search, setSearch] = useState('')

  // Group runs by domain, pick latest per domain
  const domainSummaries = useMemo(() => {
    const byDomain = new Map<
      string,
      { latest: SavedRun; previous: SavedRun | null; runCount: number }
    >()

    // Sort all runs by timestamp desc
    const sorted = [...savedRuns].sort((a, b) => b.timestamp - a.timestamp)

    for (const run of sorted) {
      const existing = byDomain.get(run.domain)
      if (!existing) {
        byDomain.set(run.domain, { latest: run, previous: null, runCount: 1 })
      } else {
        existing.runCount++
        if (!existing.previous) existing.previous = run
      }
    }

    return Array.from(byDomain.values())
      .filter(
        d =>
          !search ||
          d.latest.domain.toLowerCase().includes(search.toLowerCase()) ||
          d.latest.clientLabel?.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => b.latest.timestamp - a.latest.timestamp)
  }, [savedRuns, search])

  function exportCSV() {
    const headers = [
      'Domain',
      'Client',
      'Last Scan',
      'AI Readiness Score',
      'Grade',
      'Trend',
    ]
    const rows = domainSummaries.map(d => {
      const trend = d.previous
        ? d.latest.unifiedScore - d.previous.unifiedScore
        : 0
      return [
        d.latest.domain,
        d.latest.clientLabel || '',
        new Date(d.latest.timestamp).toISOString().split('T')[0],
        d.latest.unifiedScore,
        d.latest.unifiedGrade,
        trend > 0 ? `+${trend}` : `${trend}`,
      ]
    })

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `radar-domains-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Globe size={15} style={{ color: '#008dcc' }} />
          <h2 className='text-sm font-semibold text-foreground'>All Domains</h2>
          <span className='text-2xs font-mono text-muted-foreground'>
            {domainSummaries.length} domain
            {domainSummaries.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <div className='relative'>
            <Search
              size={12}
              className='absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground'
            />
            <input
              type='text'
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Filter domains...'
              className='h-7 pl-7 pr-3 rounded-md border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:ring-1 focus:ring-[#008dcc]/50'
            />
          </div>
          <button
            onClick={exportCSV}
            className='flex items-center gap-1 px-2.5 py-1.5 rounded-md text-2xs font-medium border border-border hover:bg-muted transition-colors text-muted-foreground'
          >
            <Download size={10} /> CSV
          </button>
        </div>
      </div>

      {domainSummaries.length === 0 ? (
        <div className='rounded-xl border border-border bg-card p-8 text-center'>
          <Globe size={24} className='mx-auto mb-3 text-muted-foreground' />
          <span className='block text-xs text-muted-foreground'>
            {search
              ? 'No domains match your filter.'
              : 'Run audits on multiple domains to see them here. Save each run to build your portfolio.'}
          </span>
        </div>
      ) : (
        <div className='rounded-xl border border-border bg-card overflow-hidden'>
          {/* Table header */}
          <div className='grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-muted/50'>
            <div className='col-span-4 text-xs font-semibold text-muted-foreground'>
              Domain
            </div>
            <div className='col-span-2 text-xs font-semibold text-center text-muted-foreground'>
              Last Scan
            </div>
            <div className='col-span-2 text-xs font-semibold text-center text-muted-foreground'>
              AI Readiness
            </div>
            <div className='col-span-2 text-xs font-semibold text-center text-muted-foreground'>
              Crawl Integrity
            </div>
            <div className='col-span-2 text-xs font-semibold text-center text-muted-foreground'>
              Trend
            </div>
          </div>

          {/* Rows */}
          {domainSummaries.map(d => {
            const gc = GRADE_COLORS[d.latest.unifiedGrade] || '#64748b'
            const trend = d.previous
              ? d.latest.unifiedScore - d.previous.unifiedScore
              : null

            // Crawl Integrity: average of crawl + robots + llms scores
            const crawlKeys = ['crawl', 'robots', 'llms']
            const crawlScores = crawlKeys
              .map(k => d.latest.toolScores[k]?.score)
              .filter((s): s is number => s !== undefined)
            const crawlIntegrity =
              crawlScores.length > 0
                ? Math.round(
                    crawlScores.reduce((a, b) => a + b, 0) / crawlScores.length
                  )
                : null
            const crawlGrade = crawlIntegrity
              ? gradeFromScore(crawlIntegrity)
              : null
            const crawlColor = crawlGrade ? GRADE_COLORS[crawlGrade] : '#64748b'

            return (
              <div
                key={d.latest.id}
                className='grid grid-cols-12 gap-2 px-4 py-2.5 border-b last:border-0 border-border/30 items-center cursor-pointer transition-colors hover:bg-muted/50'
                onClick={() => onSelectDomain(d.latest)}
              >
                <div className='col-span-4'>
                  <div className='flex items-center gap-2'>
                    <div
                      className='w-1.5 h-1.5 rounded-full flex-shrink-0'
                      style={{ background: gc }}
                    />
                    <span className='text-xs font-medium text-foreground truncate'>
                      {d.latest.domain}
                    </span>
                  </div>
                  {d.latest.clientLabel && (
                    <span className='text-2xs font-semibold px-1 py-0.5 rounded bg-primary/10 text-primary-ink ml-4'>
                      {d.latest.clientLabel}
                    </span>
                  )}
                </div>
                <div className='col-span-2 text-center'>
                  <span className='text-[11px] font-mono text-muted-foreground'>
                    {new Date(d.latest.timestamp).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <div className='col-span-2 text-center'>
                  <span className='text-sm font-black' style={{ color: gc }}>
                    {d.latest.unifiedScore}
                  </span>
                  <span
                    className='text-2xs font-bold ml-1 px-1 py-0.5 rounded'
                    style={{ background: `${gc}15`, color: gc }}
                  >
                    {d.latest.unifiedGrade}
                  </span>
                </div>
                <div className='col-span-2 text-center'>
                  {crawlIntegrity != null ? (
                    <>
                      <span
                        className='text-sm font-black'
                        style={{ color: crawlColor }}
                      >
                        {crawlIntegrity}
                      </span>
                      <span
                        className='text-2xs font-bold ml-1 px-1 py-0.5 rounded'
                        style={{
                          background: `${crawlColor}15`,
                          color: crawlColor,
                        }}
                      >
                        {crawlGrade}
                      </span>
                    </>
                  ) : (
                    <span className='text-xs text-muted-foreground'>--</span>
                  )}
                </div>
                <div className='col-span-2 text-center'>
                  {trend != null ? (
                    <span
                      className='inline-flex items-center gap-0.5 text-xs font-bold'
                      style={{
                        color:
                          trend > 0
                            ? '#22c55e'
                            : trend < 0
                              ? '#F90B8A'
                              : 'var(--muted-foreground)',
                      }}
                    >
                      {trend > 0 ? (
                        <TrendingUp size={10} />
                      ) : trend < 0 ? (
                        <TrendingDown size={10} />
                      ) : null}
                      {trend > 0 ? '+' : ''}
                      {trend}
                    </span>
                  ) : (
                    <span className='text-xs text-muted-foreground'>--</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
